import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCreateAnamnesisSecondaryDiagnosis } from '../../../../../api/generated/anamnesis-controller/anamnesis-controller.ts';
import { SecondaryDiagnosisDtoDiagnosisType } from '../../../../../api/generated/model';
import { getGetAnamnesesByPatientQueryKey } from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { getSecondaryDiagnosisTypeName } from '../../../../../utils/anamnesis.ts';

interface FormValues {
  diagnoses: {
    started: string;
    diagnosisType?: SecondaryDiagnosisDtoDiagnosisType;
  }[];
}

const emptyDiagnosisRow = {
  started: '',
  diagnosisType: undefined,
};

export function AddSecondaryDiagnoseDialog({
  open,
  onClose,
  anamnesisId,
  patientId,
}: {
  open: boolean;
  onClose: () => void;
  anamnesisId: string;
  patientId: string;
}) {
  const queryClient = useQueryClient();

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      diagnoses: [emptyDiagnosisRow],
    },
  });

  const diagnosisRows = watch('diagnoses');
  const hasIncompleteRows = diagnosisRows.some((diagnosisRow) => !diagnosisRow.diagnosisType);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'diagnoses',
  });

  const createSecondaryDiagnoseMutation = useCreateAnamnesisSecondaryDiagnosis({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetAnamnesesByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim Hinzufügen der Nebendiagnose ist ein Fehler aufgetreten');
      },
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = (data: FormValues) => {
    createSecondaryDiagnoseMutation.mutate({
      id: anamnesisId,
      data: data.diagnoses.map((diagnosis) => ({
        started: diagnosis.started || undefined,
        diagnosisType: diagnosis.diagnosisType as SecondaryDiagnosisDtoDiagnosisType,
      })),
    });
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 700, maxHeight: '90vh', overflowY: 'auto' }}>
        <ModalClose />
        <Typography level={'h4'}>Nebendiagnose hinzufügen</Typography>
        <Divider />
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack gap={2}>
            {fields.map((field, index) => (
              <FormGrid key={field.id}>
                <FormControl error={!!errors.diagnoses?.[index]?.started}>
                  <FormLabel>
                    <Typography>Seit</Typography>
                  </FormLabel>
                  <GermanDateInput
                    dateInputMode="freeText"
                    control={control}
                    name={`diagnoses.${index}.started` as const}
                  />
                </FormControl>
                <FormControl error={!!errors.diagnoses?.[index]?.diagnosisType}>
                  <FormLabel>
                    <Typography>Nebendiagnose *</Typography>
                  </FormLabel>
                  <Controller
                    name={`diagnoses.${index}.diagnosisType`}
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Select value={value} onChange={(_, value) => onChange(value)}>
                        {Object.values(SecondaryDiagnosisDtoDiagnosisType).map((type) => (
                          <Option value={type} key={type}>
                            {getSecondaryDiagnosisTypeName(type)}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.diagnoses?.[index]?.diagnosisType && (
                    <FormHelperText>Nebendiagnose muss angegeben werden</FormHelperText>
                  )}
                </FormControl>
                <IconButton
                  color={'danger'}
                  disabled={fields.length === 1}
                  onClick={() => remove(index)}
                  title={'Eintrag entfernen'}
                  type={'button'}
                  sx={{ alignSelf: 'end' }}>
                  <Icons.Delete />
                </IconButton>
              </FormGrid>
            ))}
            <Button
              color={'primary'}
              disabled={hasIncompleteRows}
              endDecorator={<Icons.Add />}
              onClick={() => append(emptyDiagnosisRow)}
              title={'Weitere Zeile hinzufügen'}
              type={'button'}
              sx={{ alignSelf: 'flex-end' }}>
              Zeile hinzufügen
            </Button>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty || hasIncompleteRows}
              isLoading={createSecondaryDiagnoseMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  align-items: start;
`;
